import React from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import TableChartIcon from '@mui/icons-material/TableChart';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { DataGridPremium } from '@mui/x-data-grid-premium';

import { AI_ANALYTICS_PATH } from '../../../../constants/aiAnalytics';
import { AI_COLORS } from '../aiAnalyticsConstants';

import AddQueryExampleDialog from './AddQueryExampleDialog';
import { AGGREGATION_FUNCTIONS, aggregationPosition } from './semanticModelGrid';
import SummaryCard from './SummaryCard';
import useSemanticModel, { QUERIES, TABLES } from './useSemanticModel';

const PAGE_HEIGHT = 'calc(100vh - 120px)';
const ACCESS_DENIED_MESSAGE = 'You do not have access to the semantic model.';

export default function SemanticModelPage() {
  const navigate = useNavigate();
  const {
    canManageSemanticModel,
    loading,
    deploying,
    error,
    deployable,
    pendingCount,
    selected,
    setSelected,
    addOpen,
    openAddDialog,
    closeAddDialog,
    draft,
    setDraft,
    saving,
    queryRows,
    tablesCount,
    grid,
    processRowUpdate,
    handleRowUpdateError,
    handleCreateExample,
    handleDeploy
  } = useSemanticModel();

  if (!canManageSemanticModel) {
    return (
      <Box sx={{ p: 3, height: PAGE_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {ACCESS_DENIED_MESSAGE}
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(AI_ANALYTICS_PATH)}>
            Back to AI Analytics
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: PAGE_HEIGHT, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(AI_ANALYTICS_PATH)} sx={{ mr: 2 }}>
          Back
        </Button>
        <Typography variant="h5" sx={{ flex: 1, fontWeight: 600 }}>
          Semantic Model
        </Typography>
        <Button
          variant="contained"
          startIcon={deploying ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          disabled={!deployable || deploying}
          onClick={handleDeploy}
          sx={{ backgroundColor: AI_COLORS.brand, '&:hover': { backgroundColor: AI_COLORS.brandDark } }}
        >
          {deploying ? 'Deploying…' : 'Deploy Semantics'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {deployable ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pendingCount} row{pendingCount === 1 ? '' : 's'} edited since the last deploy. Questions are paused until you
          deploy the semantic model.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          The semantic model is fully deployed.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <SummaryCard
          icon={<TableChartIcon />}
          label="Tables"
          count={tablesCount}
          selected={selected === TABLES}
          disabled={loading}
          onClick={() => setSelected(TABLES)}
        />
        <SummaryCard
          icon={<QuestionAnswerIcon />}
          label="Query Examples"
          count={queryRows.length}
          selected={selected === QUERIES}
          disabled={loading}
          onClick={() => setSelected(QUERIES)}
        />
      </Box>

      {selected === QUERIES && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            disabled={loading}
            sx={{
              color: AI_COLORS.brand,
              borderColor: AI_COLORS.brand,
              '&:hover': { borderColor: AI_COLORS.brandDark }
            }}
          >
            Add Query Example
          </Button>
        </Box>
      )}

      {selected ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGridPremium
            key={selected}
            rows={grid.rows}
            columns={grid.columns}
            getRowId={row => row.id}
            loading={loading}
            editMode="cell"
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleRowUpdateError}
            initialState={grid.initialState}
            aggregationFunctions={AGGREGATION_FUNCTIONS}
            getAggregationPosition={aggregationPosition}
            density="compact"
            disableRowSelectionOnClick
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">
            Select <strong>Tables</strong> or <strong>Query Examples</strong> above to view and edit the semantic model.
          </Typography>
        </Box>
      )}

      <AddQueryExampleDialog
        open={addOpen}
        draft={draft}
        saving={saving}
        onDraftChange={setDraft}
        onClose={closeAddDialog}
        onSave={handleCreateExample}
      />
    </Box>
  );
}
